import React, { useEffect, useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectStateroomConfig } from "../../../../store/selectors/shipBuilderSelectors";
import {
  setStateroomConfig,
  setStateroomAutoAssign,
  setStateroomPercentage,
  setPassengerCount,
  setPassengerCargoOverride,
} from "../../../../store/features/shipBuilderSlice";
import { formatNumber, formatCurrency } from "../../../../utils/formatting";
import { STATEROOM_TYPES } from "../../../../constants/stateroom";
import {
  LOW_BERTH_SPEC,
  EMERGENCY_LOW_BERTH_SPEC,
  COMMON_AREA_SPEC,
} from "../../../../constants/stateroom";
import { selectors as shipBuilderSelectors } from "../../../../store/selectors/shipBuilderSelectors";
import { selectors } from "../../../../store/selectors";
import { BreakawayStateroomConfig } from "../core/BreakawayStateroomConfig";

/**
 * Component for selecting and configuring ship staterooms
 * Handles staterooms, low berths, emergency low berths, and common areas
 *
 * @component
 * @example
 * ```tsx
 * <StateroomSelector />
 * ```
 */
export const StateroomSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const stateroomConfig = useAppSelector(selectStateroomConfig);
  const stewardsRequired = useAppSelector(
    shipBuilderSelectors.selectStewardsRequired,
  );

  // Breakaway hulls detection
  const hull = useAppSelector((state) => state.shipBuilder.hull);
  const [showBreakawayConfig, setShowBreakawayConfig] = useState(false);
  
  const hasBreakawayHulls = hull?.additionalType?.type === "breakaway" && 
                           hull?.breakawayConfig?.sections && 
                           hull.breakawayConfig.sections.length > 0;

  const getBreakawayInfo = () => {
    if (!hull?.breakawayConfig?.sections) return "";
    const sections = hull.breakawayConfig.sections;
    return `${sections.length} section${sections.length > 1 ? 's' : ''} (${sections.map(s => `${s.name || `Section ${s.sectionNumber}`}: ${s.size} tons`).join(", ")})`;
  };

  // Use selectors for all summary/derived values
  const assignedCrewCount = useAppSelector(
    selectors.derived.selectAssignedCrewCount,
  );
  const stateroomNeeds = (useAppSelector(
    selectors.derived.selectStateroomNeeds,
  ) || {}) as any;

  const totalPassengerCargoTons = useAppSelector(
    selectors.derived.selectTotalPassengerCargoTons,
  ) as number;
  const totalStaterooms = useAppSelector(
    selectors.derived.selectTotalStaterooms,
  ) as number;
  const totalCapacity = useAppSelector(
    selectors.derived.selectTotalStateroomCapacity,
  ) as number;
  const optionalCommonAreaTonnage = useAppSelector(
    selectors.derived.selectOptionalSystemsCommonAreaTonnage,
  ) as number;
  const recommendedCommonArea = useAppSelector(
    selectors.derived.selectRecommendedCommonArea,
  ) as number;

  const showCommonAreaWarning = useAppSelector(
    selectors.derived.selectShowCommonAreaWarning,
  );



  // New selector for comprehensive hierarchical stateroom allocation
  const hierarchicalAllocation = useAppSelector(
    selectors.derived.selectHierarchicalStateroomAllocation,
  );

  // Selectors for accommodation breakdown
  const highStateroomQuantity = useAppSelector((state) => {
    const accommodations = state.optionalSystems.accommodations || [];
    return accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          s.subtype === "highStateroom",
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );
  });

  const luxuryStateroomQuantity = useAppSelector((state) => {
    const accommodations = state.optionalSystems.accommodations || [];
    return accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          s.subtype === "luxuryStateroom",
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );
  });

  const barracksCapacity = useAppSelector((state) => {
    const accommodations = state.optionalSystems.accommodations || [];
    return accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          s.subtype === "barracks",
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );
  });

  // --- Handler functions ---
  const handleAutoAssignToggle = (enabled: boolean) => {
    dispatch(setStateroomAutoAssign(enabled));
  };

  const handlePercentageChange = (value: number) => {
    dispatch(setStateroomPercentage(Math.max(0, Math.min(200, value))));
  };

  const handleStateroomTypeChange = useCallback(
    (type: "single" | "double", value: number) => {
      if (stateroomConfig) {
        dispatch(
          setStateroomConfig({
            ...stateroomConfig,
            [type]: value,
          }),
        );
      }
    },
    [stateroomConfig, dispatch],
  );

  const handleLowBerthChange = (value: number) => {
    if (stateroomConfig) {
      dispatch(
        setStateroomConfig({
          ...stateroomConfig,
          lowBerths: {
            ...stateroomConfig.lowBerths,
            count: value,
          },
        }),
      );
    }
  };

  const handleEmergencyLowBerthChange = (value: number) => {
    if (stateroomConfig) {
      dispatch(
        setStateroomConfig({
          ...stateroomConfig,
          emergencyLowBerths: {
            ...stateroomConfig.emergencyLowBerths,
            count: value,
          },
        }),
      );
    }
  };

  const handleCommonAreaChange = (value: number) => {
    if (stateroomConfig) {
      dispatch(
        setStateroomConfig({
          ...stateroomConfig,
          commonArea: value,
        }),
      );
    }
  };

  const handlePassengerCountChange = (
    type: "high" | "middle" | "basic" | "low",
    value: number,
  ) => {
    dispatch(setPassengerCount({ type, count: value }));
  };

  const handleCargoOverrideChange = (
    type: "high" | "middle" | "basic" | "low",
    value: number,
  ) => {
    dispatch(setPassengerCargoOverride({ type, value }));
  };

  const resetCargoToDefaults = () => {
    dispatch(setPassengerCargoOverride({ type: "high", value: 1 }));
    dispatch(setPassengerCargoOverride({ type: "middle", value: 100 }));
    dispatch(setPassengerCargoOverride({ type: "basic", value: 10 }));
    dispatch(setPassengerCargoOverride({ type: "low", value: 10 }));
  };

  // Initialize default config if missing
  useEffect(() => {
    if (!stateroomConfig) {
      dispatch(
        setStateroomConfig({
          single: 0,
          double: 0,
          lowBerths: {
            count: 0,
            cost: 0,
            tons: 0,
            power: 0,
            capacity: 0,
            techLevel: 0,
            maintenanceCost: 0,
          },
          emergencyLowBerths: {
            count: 0,
            cost: 0,
            tons: 0,
            power: 0,
            capacity: 0,
            techLevel: 0,
            maintenanceCost: 0,
          },
          commonArea: 0,
          totalCost: 0,
          totalTons: 0,
          totalPower: 0,
          comfort: 1,
          warnings: [],
          autoAssignStaterooms: true,
          stateroomPercentage: 100,
        }),
      );
    }
  }, [dispatch, stateroomConfig]);

  // --- Manual stateroom control (no auto-sync) ---
  // Removed automatic linking between passenger counts and stateroom allocation
  // Users now control stateroom counts independently

  return (
    <div className="flex flex-col md:flex-row gap-8">
      {/* Breakaway Hulls Detection */}
      {hasBreakawayHulls && (
        <div className="w-full mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  Breakaway Hulls Detected
                </h3>
                <p className="text-sm text-blue-800 mb-2">
                  {getBreakawayInfo()}
                </p>
                <p className="text-xs text-blue-700">
                  <strong>Note:</strong> Staterooms must be assigned to specific breakaway sections. 
                  Totals are calculated for the whole ship.
                </p>
              </div>
              <button
                onClick={() => setShowBreakawayConfig(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Configure Section Staterooms
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Left Column: Inputs */}
      <div className="flex-1 space-y-6">
        {/* Top Row: Passenger & Crew Input and Living Spaces side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Passenger Counts */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 shadow">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <span className="text-blue-500">👥</span> Passenger Counts
            </h3>
            {/* Passenger Passage Types (existing inputs) */}
            <div className="space-y-4">
              {/* High Passage */}
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      High Passage
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.highCount}
                      onChange={(e) =>
                        handlePassengerCountChange(
                          "high",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">
                      Cargo Allocation (tons)
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.highCargo}
                      onChange={(e) =>
                        handleCargoOverrideChange(
                          "high",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={0.1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100 text-xs"
                    />
                  </div>
                </div>
              </div>
              {/* Middle Passage */}
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Middle Passage
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.middleCount}
                      onChange={(e) =>
                        handlePassengerCountChange(
                          "middle",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">
                      Cargo Override (kg)
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.middleCargo}
                      onChange={(e) =>
                        handleCargoOverrideChange(
                          "middle",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100 text-xs"
                    />
                  </div>
                </div>
              </div>
              {/* Basic Passage */}
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Basic Passage
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.basicCount}
                      onChange={(e) =>
                        handlePassengerCountChange(
                          "basic",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">
                      Cargo Override (kg)
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.basicCargo}
                      onChange={(e) =>
                        handleCargoOverrideChange(
                          "basic",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100 text-xs"
                    />
                  </div>
                </div>
              </div>
              {/* Low Passage */}
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Low Passage
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.lowCount}
                      onChange={(e) =>
                        handlePassengerCountChange(
                          "low",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">
                      Cargo Override (kg)
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.lowCargo}
                      onChange={(e) =>
                        handleCargoOverrideChange("low", Number(e.target.value))
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100 text-xs"
                    />
                  </div>
                </div>
              </div>
              <button
                onClick={resetCargoToDefaults}
                className="w-full mt-2 px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded text-gray-900 dark:text-gray-100"
              >
                Reset Cargo to Defaults
              </button>
            </div>
          </div>

          {/* Living Spaces */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 shadow">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <span className="text-green-500">🏠</span> Living Spaces
            </h3>

            {/* Auto-Assignment Controls */}
            <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-center justify-between mb-3">
                {/* Left: Default auto-assign coverage */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Auto-assign coverage:</label>
                  <input
                    type="number"
                    value={stateroomConfig?.stateroomPercentage ?? 100}
                    onChange={(e) => handlePercentageChange(Number(e.target.value))}
                    disabled={!(stateroomConfig?.autoAssignStaterooms)}
                    min={0}
                    max={200}
                    step={5}
                    className="w-20 p-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">%</span>
                </div>
                {/* Right: Manual override toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    aria-label="Manually Override Stateroom Allocations"
                    title="Manually Override Stateroom Allocations"
                    checked={!(stateroomConfig?.autoAssignStaterooms ?? true)}
                    onChange={(e) => handleAutoAssignToggle(!e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Manually Override Stateroom Allocations
                  </label>
                </div>
              </div>
              {stateroomConfig?.autoAssignStaterooms && (
                <div className="text-xs text-blue-800 dark:text-blue-300">
                  <p>
                    ✓ Auto-assigning {Math.ceil((assignedCrewCount * (stateroomConfig?.stateroomPercentage ?? 100)) / 100)} staterooms 
                    for {assignedCrewCount} crew members ({stateroomConfig?.stateroomPercentage ?? 100}% coverage)
                  </p>
                  {stateroomNeeds.highCount > 0 && (
                    <p className="mt-1">
                      ✓ High passengers: {stateroomNeeds.highCount} single staterooms
                    </p>
                  )}
                  {stateroomNeeds.middleCount > 0 && (
                    <p className="mt-1">
                      ✓ Middle passengers: {stateroomNeeds.middleCount} single staterooms
                    </p>
                  )}
                  {stateroomNeeds.basicCount > 0 && (
                    <p className="mt-1">
                      ✓ Basic passengers: {Math.ceil(stateroomNeeds.basicCount / 2)} double occupancy staterooms
                    </p>
                  )}
                  {stateroomNeeds.lowCount > 0 && (
                    <p className="mt-1">
                      ✓ Low passengers: {stateroomNeeds.lowCount} low berths
                    </p>
                  )}
                  <p className="mt-1">
                    ✓ Common areas automatically calculated ({stateroomConfig?.commonArea || 0} tons)
                  </p>
                </div>
              )}
              {!stateroomConfig?.autoAssignStaterooms && (
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Manual override enabled. Configure staterooms manually below.
                </div>
              )}
            </div>

            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Stateroom (single occupancy)
                </label>
                <input
                  type="number"
                  value={stateroomConfig?.single || 0}
                  onChange={(e) => {
                    handleStateroomTypeChange("single", Number(e.target.value));
                  }}
                  disabled={stateroomConfig?.autoAssignStaterooms ?? true}
                  min={0}
                  step={1}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Stateroom (double occupancy)
                </label>
                <input
                  type="number"
                  value={stateroomConfig?.double || 0}
                  onChange={(e) => {
                    handleStateroomTypeChange("double", Number(e.target.value));
                  }}
                  disabled={stateroomConfig?.autoAssignStaterooms ?? true}
                  min={0}
                  step={1}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {/* Accommodation Breakdown */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    High Staterooms
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {highStateroomQuantity}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Luxury Staterooms
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {luxuryStateroomQuantity}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Barracks
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {barracksCapacity}
                  </div>
                </div>
              </div>
            </div>

            {/* Horizontal info bar */}
            <div className="flex flex-wrap gap-4 mt-6 text-sm justify-between">
              <div>
                <span className="block text-gray-500 dark:text-gray-400">Total Staterooms</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">{totalStaterooms}</span>
              </div>
              <div>
                <span className="block text-gray-500 dark:text-gray-400">Total Capacity</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">{totalCapacity} people</span>
              </div>
            </div>

            {/* Low Berths Section */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <h4 className="text-md font-medium mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
                <span className="text-yellow-500">📦</span> Low Berths
              </h4>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Number of Low Berths
                </label>
                <input
                  type="number"
                  value={stateroomConfig?.lowBerths?.count || 0}
                  onChange={(e) => handleLowBerthChange(Number(e.target.value))}
                  min={0}
                  step={1}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100 dark:bg-gray-600 dark:text-gray-100 dark:border-gray-500"
                />
              </div>
            </div>
          </div>
        </div>
        {/* Common Areas */}
        <div className="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4 shadow">
          <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <span className="text-purple-500">👥</span> Common Areas
          </h3>
          <div className="mb-4 flex gap-6 items-start">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Common Area Size (tons)
              </label>
              <input
                type="number"
                value={stateroomConfig?.commonArea || 0}
                onChange={(e) => handleCommonAreaChange(Number(e.target.value))}
                disabled={stateroomConfig?.autoAssignStaterooms ?? true}
                min={0}
                step={1}
                className="w-32 p-2 border rounded dark:bg-gray-600 dark:text-gray-100 dark:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {optionalCommonAreaTonnage > 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Common Area (Tons) from Optional Systems:{" "}
                  {formatNumber(optionalCommonAreaTonnage)}
                </p>
              )}
            </div>

            {stateroomConfig && (
              <div className="flex-1 space-y-2 text-sm">
                <p>
                  Recommended Size:{" "}
                  <span
                    className={`font-medium ${showCommonAreaWarning ? "text-red-600" : "text-green-600"}`}
                  >
                    {formatNumber(recommendedCommonArea || 0)} tons
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
        {/* Emergency Low Berths */}
        <div className="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4 shadow">
          <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <span className="text-red-500">⚠️</span> Emergency Low Berths
          </h3>
          <div className="mb-4 flex gap-6 items-start">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Number of Emergency Low Berths
              </label>
              <input
                type="number"
                value={stateroomConfig?.emergencyLowBerths?.count || 0}
                onChange={(e) =>
                  handleEmergencyLowBerthChange(Number(e.target.value))
                }
                min={0}
                step={1}
                className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100"
              />
              {stateroomConfig && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Emergency Capacity:{" "}
                  {(stateroomConfig.emergencyLowBerths?.count || 0) *
                    EMERGENCY_LOW_BERTH_SPEC.capacity}{" "}
                  People
                </p>
              )}
            </div>

          </div>
        </div>
      </div>
      {/* Right Column: Sticky Summary */}
      <div className="w-full md:w-96 md:sticky md:top-4 h-fit">
        <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded shadow space-y-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <span className="text-green-500">📊</span> Accommodation Summary
          </h4>

          {/* Crew Requirements Section */}
          <div className="bg-white dark:bg-gray-800 p-3 rounded border-l-4 border-indigo-500">
            <h5 className="font-medium text-indigo-700 dark:text-indigo-400 mb-2">
              Crew Requirements
            </h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Total Crew:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{Number(assignedCrewCount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Crew Barracks Accommodation:</span>
                <span
                  className={`font-medium ${useAppSelector(selectors.derived.selectBarracksCrewCapacity) > 0 ? "text-green-600 dark:text-green-400" : "text-gray-900 dark:text-gray-100"}`}
                >
                  {useAppSelector(selectors.derived.selectBarracksCrewCapacity)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Crew Needing Staterooms:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {hierarchicalAllocation.totals.crew + hierarchicalAllocation.shortfalls.crew}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Luxury Staterooms:</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {hierarchicalAllocation.allocations.crew.luxury}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">High Staterooms:</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {hierarchicalAllocation.allocations.crew.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Stateroom (single):</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.allocations.crew.single}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Stateroom (double):</span>
                <span className="font-medium text-orange-600 dark:text-orange-400">
                  {hierarchicalAllocation.allocations.crew.double} ({hierarchicalAllocation.accommodated.crew.double} people)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Barracks:</span>
                <span className="font-medium text-yellow-600 dark:text-yellow-400">
                  {hierarchicalAllocation.allocations.crew.barracks}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Total Allocated:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.totals.crew}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Still Needed:</span>
                <span
                  className={`font-medium ${hierarchicalAllocation.shortfalls.crew > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                >
                  {hierarchicalAllocation.shortfalls.crew}
                </span>
              </div>
            </div>
          </div>

          {/* High Passage Section */}
          <div className="bg-white dark:bg-gray-800 p-3 rounded border-l-4 border-blue-500">
            <h5 className="font-medium text-blue-700 dark:text-blue-400 mb-2">High Passage</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Passengers:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {hierarchicalAllocation.totals.high + hierarchicalAllocation.shortfalls.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Luxury Staterooms:</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {hierarchicalAllocation.allocations.high.luxury}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">High Staterooms:</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {hierarchicalAllocation.allocations.high.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Stateroom (single):</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.allocations.high.single}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Total Allocated:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.totals.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Still Needed:</span>
                <span
                  className={`font-medium ${hierarchicalAllocation.shortfalls.high > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                >
                  {hierarchicalAllocation.shortfalls.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Stewards Required:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{stewardsRequired.high}</span>
              </div>
            </div>
          </div>

          {/* Middle Passage Section */}
          <div className="bg-white dark:bg-gray-800 p-3 rounded border-l-4 border-green-500">
            <h5 className="font-medium text-green-700 dark:text-green-400 mb-2">Middle Passage</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Passengers:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {hierarchicalAllocation.totals.middle + hierarchicalAllocation.shortfalls.middle}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Luxury Staterooms:</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {hierarchicalAllocation.allocations.middle.luxury}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">High Staterooms:</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {hierarchicalAllocation.allocations.middle.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Stateroom (single):</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.allocations.middle.single}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Total Allocated:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.totals.middle}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Still Needed:</span>
                <span
                  className={`font-medium ${hierarchicalAllocation.shortfalls.middle > 0 ? "text-red-600" : "text-green-600"}`}
                >
                  {hierarchicalAllocation.shortfalls.middle}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Stewards Required:</span>
                <span className="font-medium">{stewardsRequired.middle}</span>
              </div>
            </div>
          </div>

          {/* Basic Passage Section */}
          <div className="bg-white dark:bg-gray-800 p-3 rounded border-l-4 border-orange-500">
            <h5 className="font-medium text-orange-700 dark:text-orange-400 mb-2">Basic Passage</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Passengers:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {hierarchicalAllocation.totals.basic + hierarchicalAllocation.shortfalls.basic}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Luxury Staterooms:</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {hierarchicalAllocation.allocations.basic.luxury}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">High Staterooms:</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {hierarchicalAllocation.allocations.basic.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Stateroom (single):</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.allocations.basic.single}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Stateroom (double):</span>
                <span className="font-medium text-orange-600 dark:text-orange-400">
                  {hierarchicalAllocation.allocations.basic.double} ({hierarchicalAllocation.accommodated.basic.double} people)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Barracks:</span>
                <span className="font-medium text-yellow-600 dark:text-yellow-400">
                  {hierarchicalAllocation.allocations.basic.barracks}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Cargo Space Cots:</span>
                <span className="font-medium text-red-600 dark:text-red-400">
                  {hierarchicalAllocation.allocations.basic.cots}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Total Allocated:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.totals.basic}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Still Needed:</span>
                <span
                  className={`font-medium ${hierarchicalAllocation.shortfalls.basic > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                >
                  {hierarchicalAllocation.shortfalls.basic}
                </span>
              </div>
            </div>
          </div>

          {/* Low Berth Section */}
          <div className="bg-white dark:bg-gray-800 p-3 rounded border-l-4 border-red-500">
            <h5 className="font-medium text-red-700 dark:text-red-400 mb-2">Low Berth</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Passengers:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {hierarchicalAllocation.totals.low + hierarchicalAllocation.shortfalls.low}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Low Berths Allocated:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.allocations.low.lowBerths}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Still Needed:</span>
                <span
                  className={`font-medium ${hierarchicalAllocation.shortfalls.low > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                >
                  {hierarchicalAllocation.shortfalls.low}
                </span>
              </div>
            </div>
          </div>

          {/* Passenger Cargo Reserve */}
          <div className="bg-white dark:bg-gray-800 p-3 rounded border-l-4 border-gray-500">
            <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
              Passenger Cargo Reserve
            </h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Total Cargo Space Required:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {formatNumber(totalPassengerCargoTons)} tons
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Sticky Footer Summary remains at the bottom as before */}
      {!hasBreakawayHulls && stateroomConfig && (
        <footer className="fixed bottom-0 left-0 right-0 z-20 bg-gray-800 text-white shadow-lg border-t border-gray-700">
          <div className="container mx-auto grid grid-cols-3 gap-4 items-center text-center px-6 py-4">
            <div>
              <span className="text-gray-400 block text-sm font-semibold">
                Total Cost
              </span>
              <span className="text-xl font-bold text-green-400">
                {formatCurrency(
                  // Staterooms cost
                  (stateroomConfig.single || 0) *
                    STATEROOM_TYPES.STANDARD.cost +
                    (stateroomConfig.double || 0) *
                      STATEROOM_TYPES.STANDARD.cost +
                    // Low berths cost
                    (stateroomConfig.lowBerths?.count || 0) *
                      LOW_BERTH_SPEC.cost +
                    // Emergency low berths cost
                    (stateroomConfig.emergencyLowBerths?.count || 0) *
                      EMERGENCY_LOW_BERTH_SPEC.cost +
                    // Common area cost
                    (stateroomConfig.commonArea || 0) *
                      COMMON_AREA_SPEC.costPerTon,
                )}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm font-semibold">
                Total Tons
              </span>
              <span className="text-xl font-bold text-blue-400">
                {formatNumber(
                  // Staterooms tons
                  (stateroomConfig.single || 0) *
                    STATEROOM_TYPES.STANDARD.tons +
                    (stateroomConfig.double || 0) *
                      STATEROOM_TYPES.STANDARD.tons +
                    // Low berths tons
                    (stateroomConfig.lowBerths?.count || 0) *
                      LOW_BERTH_SPEC.tons +
                    // Emergency low berths tons
                    (stateroomConfig.emergencyLowBerths?.count || 0) *
                      EMERGENCY_LOW_BERTH_SPEC.tons +
                    // Common area tons
                    (stateroomConfig.commonArea || 0),
                )}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm font-semibold">
                Total Power
              </span>
              <span className="text-xl font-bold text-yellow-400">
                {
                  // Staterooms power (0 for standard)
                  (stateroomConfig.single || 0) *
                    STATEROOM_TYPES.STANDARD.power +
                    (stateroomConfig.double || 0) *
                      STATEROOM_TYPES.STANDARD.power +
                    // Low berths power (1 per 10 berths)
                    Math.ceil((stateroomConfig.lowBerths?.count || 0) / 10) +
                    // Emergency low berths power
                    (stateroomConfig.emergencyLowBerths?.count || 0) *
                      EMERGENCY_LOW_BERTH_SPEC.power +
                    // Common area power
                    (stateroomConfig.commonArea || 0) *
                      COMMON_AREA_SPEC.powerPerTon
                }
              </span>
            </div>
          </div>
        </footer>
      )}

      {/* Breakaway Stateroom Configuration Modal */}
      {showBreakawayConfig && (
        <BreakawayStateroomConfig
          isOpen={showBreakawayConfig}
          onClose={() => setShowBreakawayConfig(false)}
        />
      )}
    </div>
  );
};
